import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { isLanguageSupported, supportedLanguages } from '@/plugins/i18n'
import { useLanguageService } from '@/composables/language'
import { isUserLoggedIn } from '@/auth/utils'
import { publicPages } from '@/router/public-pages'

export function languageMiddleware(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
  const languageService = useLanguageService()

  languageService.initializeLanguage()
  next()
}

export function isAuthRequired(to: RouteLocationNormalized) {
  return !publicPages.reduce(
    (a, b) => a || RegExp(b).test(<string>to.name),
    false,
  )
}

export function authMiddleware(to: RouteLocationNormalized) {
  if (isAuthRequired(to) && !isUserLoggedIn())
    return { name: 'login', query: { to: to.name !== 'index' ? to.fullPath : undefined } }
}

const languagePrefixRegex = new RegExp(`^/(${supportedLanguages.join('|')})(/|$)`)

async function ensureLanguageInitialized(
  service: ReturnType<typeof useLanguageService>,
) {
  // Assuming service._initialized is a ComputedRef<boolean> from useLanguageService
  if (service._initialized && !service._initialized.value)
    await service.initializeLanguage()
}

export async function languagePrefixMiddleware(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  const languageService = useLanguageService()
  const path = to.path
  const match = path.match(languagePrefixRegex)

  if (!match) {
    await ensureLanguageInitialized(languageService)

    return next()
  }

  const langFromFile = match[1]

  if (!isLanguageSupported(langFromFile)) {
    await ensureLanguageInitialized(languageService)

    return next()
  }

  if (languageService.currentLanguage.value !== langFromFile)
    await languageService.setLanguage(langFromFile as any)

  let newPath = path.substring(match[0].length - (match[2] === '/' ? 1 : 0))
  if (!newPath.startsWith('/'))
    newPath = `/${newPath}`

  if (newPath === '')
    newPath = '/'

  return next({
    path: newPath,
    query: to.query,
    hash: to.hash,
    replace: true,
  })
}
